import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import Spinner from '~/src/components/Spinner';
import { Form, renderFormField } from '~/src/components/Forms';
import analyticsService from '~/src/services/analytics';
import { tryConvertToNumber } from '../../../utils';
import { useLayoutContext } from '~/src/contexts';
import { LAYOUT_TOAST_TYPES } from '~/src/components/PageLayout/Toasts';
import { usePrepopulateQuestionnaireResponses } from '~/src/entities/questionnaires/hooks/usePrepopulateQuestionnaireResponses';
import { type QuestionnaireSubmission } from '@clio/questionnaire-builder';
import { useCurrentOrgFprint } from '~/src/entities/user';
import { usePersistRelatedQuestionnaireSubmission } from '~/src/entities/questionnaires/hooks';
import { copyTextToClipboard } from '~/src/utils/clipboard';

export const GeneratedSubmissionLink: React.FC<{
  questionnaireId: string;
  matterId: string;
  submissionId: string;
  submissionTitle: string | null;
  questionnaireLink: string | undefined;
  questionnaireSubmission: QuestionnaireSubmission;
  isLinkLoading: boolean;
  isLinkError: boolean;
}> = ({
  questionnaireId,
  matterId,
  submissionId,
  submissionTitle,
  questionnaireLink,
  questionnaireSubmission,
  isLinkLoading,
  isLinkError,
}) => {
  const orgFprint = useCurrentOrgFprint();
  const persistRelatedQuestionnaireSubmission =
    usePersistRelatedQuestionnaireSubmission(orgFprint);

  const { showToast } = useLayoutContext();
  const {
    setMatterId: setMatterIdForPrepopulate,
    setSubmissionId,
    isPrepopulatingDone,
    isPrepopulatingLoading,
  } = usePrepopulateQuestionnaireResponses(orgFprint);

  useEffect(() => {
    setMatterIdForPrepopulate(matterId);
    setSubmissionId(submissionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (questionnaireSubmission) {
      // save to clientmatter_relatedquestionnairesubmission table in clio-draft-backend database
      const payload = {
        questionnaire_submission_id: questionnaireSubmission.id,
        questionnaire_id: questionnaireSubmission.questionnaire_id,
        questionnaire_version_id:
          questionnaireSubmission.questionnaire_version.id,
        title: submissionTitle,
        created_at: questionnaireSubmission.created_at,
        project_id: null,
        matter_id: matterId,
        data: {
          questions: [],
        },
      };
      persistRelatedQuestionnaireSubmission.mutate(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopyLink = () => {
    if (!questionnaireLink) return;

    analyticsService.track('Copied Share Link', {
      questionnaire_Id: tryConvertToNumber(questionnaireId),
      matter_id: tryConvertToNumber(matterId),
      page: 'questionnaire_builder_page',
    });
    copyTextToClipboard(questionnaireLink).then(() => {
      showToast(LAYOUT_TOAST_TYPES.success, {
        message: 'Link copied to clipboard!',
      });
    });
  };

  const fields = [
    {
      id: 'link',
      type: 'copyLink',
      target: '_blank',
      defaultValue: questionnaireLink,
      onClick: handleCopyLink,
    },
  ];

  const fieldsProp = fields.map((field) => renderFormField(field));

  if (isLinkLoading || isPrepopulatingLoading || !isPrepopulatingDone) {
    return (
      <Box
        sx={{
          paddingTop: '16px',
          width: '100%',
        }}
        data-testid="spinner"
      >
        <Spinner />
      </Box>
    );
  }

  if (isLinkError || !questionnaireLink) {
    return (
      <Typography variant="h3">
        An error occurred, please try again later...
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        paddingTop: '8px',
        width: '100%',
      }}
    >
      <Typography variant="h2" paddingBottom={'4px'}>
        Questionnaire link
      </Typography>
      <Form
        fields={fieldsProp}
        onChange={() => {}}
        className={undefined}
        focusFirstInput={undefined}
        focusTimeout={undefined}
      />
    </Box>
  );
};
